import { IconButton } from '@kanbu/chatbot-ui';
import { Trans } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { MessageSquarePlus } from 'lucide-react';
import { memo, useEffect, useState } from 'react';
import { v7 as uuidV7 } from 'uuid';
import { useShallow } from 'zustand/react/shallow';

import { useChatConfig } from '@/contexts/ChatConfigProvider';
import { chatKeys, threadKeys } from '@/services/queryClient';
import { useBoundStore } from '@/store/store';

import { SimpleTooltip } from '../tooltip/SimpleTooltip.js';

type NewThreadProps = {
  disabled?: boolean;
  onReset?: () => void;
};

export const NewThread = memo(
  ({ disabled = false, onReset }: NewThreadProps) => {
    const { chatId } = useChatConfig();
    const [isLoading, setIsLoading] = useState(false);
    const {
      setThreadId,
      resetFAQLoop,
      pushRoute,
      resetConversation,
      setResetConversation,
    } = useBoundStore(
      useShallow(state => ({
        setThreadId: state.setThreadId,
        resetFAQLoop: state.resetFAQLoop,
        pushRoute: state.pushRoute,
        resetConversation: state.resetConversation,
        setResetConversation: state.setResetConversation,
      })),
    );

    useEffect(() => {
      if (resetConversation) {
        const timer = setTimeout(() => {
          setResetConversation(false);
        }, 10000);

        return () => clearTimeout(timer);
      }
    }, [resetConversation, setResetConversation]);

    /**
     * Create new thread to reset the conversation.
     */
    const queryClient = useQueryClient();

    /**
     * Reset conversation by creating new one and invalidating
     * the chat query storage.
     */
    const handleReset = async () => {
      onReset?.();
      setIsLoading(true);

      try {
        // Push route to chat
        pushRoute('chat');

        // Initialize new thread
        setThreadId(uuidV7());

        // Reset FAQLoop
        resetFAQLoop();

        // Invalidate chat query storage.
        queryClient.invalidateQueries({
          queryKey: chatKeys.detail(chatId),
        });

        // Invalidate thread query storage.
        queryClient.invalidateQueries({
          queryKey: threadKeys.chatLists(chatId),
        });
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    return resetConversation ? (
      <SimpleTooltip
        title={
          <Trans>
            Is this a new topic or are you not satisfied with the answer? Click
            on this button and try again.
          </Trans>
        }
        className='max-w-56'
        open={resetConversation}
        asChild
        data-testid='new-thread-button-reset-tooltip'
      >
        <IconButton
          variant='ghost'
          onClick={handleReset}
          disabled={disabled || isLoading}
          data-testid='new-thread-button'
        >
          <MessageSquarePlus />
        </IconButton>
      </SimpleTooltip>
    ) : (
      <SimpleTooltip
        title={<Trans>Start new conversation</Trans>}
        asChild
        data-testid='new-thread-button-tooltip'
      >
        <IconButton
          variant='ghost'
          onClick={handleReset}
          disabled={disabled || isLoading}
          data-testid='new-thread-button'
        >
          <MessageSquarePlus />
        </IconButton>
      </SimpleTooltip>
    );
  },
);
